"use client"

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import SearchIcon from "@mui/icons-material/Search"
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  useTheme,
  Paper,
  TextField,
  InputAdornment,
} from "@mui/material"
import type React from "react"
import { useState } from "react"
import { toast } from "react-toastify"
import useShortForm from "../formik/useShortForm"
import type { Image } from "../types"
import { useAppDispatch, useAppSelector } from "store"
import DialogBox from "components/shared/modal/DialogBox"
import ImageUploader from "components/shared/ImageUploder/ImageUploder"
import { uploadSingleImage } from "store/thunks/imageThunks"
import { uploadShorts } from "store/thunks/shortsThunk"
import { TRIP_STATUS, validateVideoFile } from "utils/index"
import { getShorts } from "services/shortService"
import { forceRefreshShorts } from "slice/shortSlice"

const MAX_VIDEO_SIZE_MB = 25
const MAX_VIDEO_SECONDS = 90
const THUMB_RATIO = 9 / 16
const THUMB_RATIO_RANGE = [THUMB_RATIO - 0.04, THUMB_RATIO + 0.04]
const MAX_CAPTION_LENGTH = 300
const STEPS = ["Trip", "Thumbnail", "Video", "Caption", "Review"]

/**
 * Sanitize caption: remove HTML tags but preserve line breaks
 */
const sanitizeCaption = (text: string): string => {
  // Remove HTML tags
  let sanitized = text.replace(/<[^>]*>/g, "");
  // Decode HTML entities
  sanitized = sanitized
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  // Limit to 300 characters
  return sanitized.slice(0, MAX_CAPTION_LENGTH);
};

const ShortsModal: React.FC<{ open: boolean; setOpen: (open: boolean) => void }> = ({ open, setOpen }) => {
  const dispatch = useAppDispatch()
  const { formik } = useShortForm()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.down("md"))

  const userDetails = useAppSelector((state) => state.appLayout.userDetails) as any
  const trips = useAppSelector((state) => state.trips.tripCache[TRIP_STATUS.APPROVED]) || []
  const merchantId = userDetails?.userId || ""

  const [selectedTripId, setSelectedTripId] = useState<string>("");
  const [tripSearchQuery, setTripSearchQuery] = useState<string>("");

  const [step, setStep] = useState<number>(0)
  const [thumbnail, setThumbnail] = useState<Image | null>(null)
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [videoDuration, setVideoDuration] = useState<number | null>(null)
  const [caption, setCaption] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Filter and sort trips based on search query
  const filteredTrips = trips
    .filter((trip: any) => {
      const tripTitle = (trip.title ?? trip.tripTitle ?? "").toLowerCase();
      return tripTitle.includes(tripSearchQuery.toLowerCase());
    })
    .sort((a: any, b: any) => {
      const titleA = (a.title ?? a.tripTitle ?? "").toLowerCase();
      const titleB = (b.title ?? b.tripTitle ?? "").toLowerCase();
      return titleA.localeCompare(titleB);
    });

  const resetAll = () => {
    setStep(0)
    setThumbnail(null)
    setVideoFile(null)
    setVideoDuration(null)
    setCaption("")
    setTripSearchQuery("")
    formik.resetForm()
  };

  const handleTripDetails = (e: any) => {
    const tripId = String(e.target.value);
    formik.setFieldValue("tripId", tripId);
    setSelectedTripId(tripId);
    setTripSearchQuery(""); // Clear search on selection

    // Find the selected trip from the trips array
    const selectedTrip = trips.find((t: any) => (t.tripId ?? t.id) === tripId);

    if (selectedTrip) {
      // Save trip search ID key and search ID from selected trip
      formik.setFieldValue("searchIdKey", selectedTrip.searchIdKey || "");
      formik.setFieldValue("searchId", selectedTrip.searchId || "");
    } else {
      // Clear if no trip found
      formik.setFieldValue("searchIdKey", "");
      formik.setFieldValue("searchId", "");
    }
  }

  const handleTripNext = async () => {
    if (!formik.values.tripId) {
      toast.error("Please select a trip")
      return
    };
    try {
      const response = await getShorts(selectedTripId);

      // Calculate video index based on existing memories
      const nextIndex =  response?.length ? response.length + 1 : 1;
      const paddedIndex = String(nextIndex).padStart(2, "0"); // Format as 01, 02, etc.
      formik.setFieldValue("thumbnailAltText", paddedIndex);
      setStep(1);
      
    } catch (error) {
      console.error("[v0] Error handling trip next:", error);
      toast.error("An error occurred. Please try again.");
      return;
    }
  }

  const handleThumbnailNext = () => {
    if (!thumbnail) {
      toast.error("Please select a thumbnail")
      return
    }
    setStep(2)
  }

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const validation = await validateVideoFile(file, MAX_VIDEO_SIZE_MB, MAX_VIDEO_SECONDS)
      if (!validation.valid) {
        toast.error(validation.error)
        return
      }
      setVideoFile(file)
      setVideoDuration(validation.duration || null)
      toast.success(`Video selected: ${Math.round(validation.duration || 0)}s`)
    } catch (err: any) {
      toast.error(err?.message || "Invalid video file")
    }
    e.target.value = ""
  }

  const handleVideoNext = () => {
    if (!videoFile) {
      toast.error("Please select a video")
      return
    }
    setStep(3)
  }

  const handleCaptionNext = () => {
    // Caption is optional, so we can proceed regardless
    formik.setFieldValue("caption", caption);
    setStep(4)
  };

  /**
   * Generate filename with index
   */
  const generateFileName = (
    baseId: string,
    type: "thumbnail" | "memories",
    originalName: string
  ): string => {
    const ext = originalName.split(".").pop() || (type === "thumbnail" ? "jpg" : "mp4");
    return `${baseId}-${type}-${formik.values.thumbnailAltText}.${ext}`;
  };

  /**
   * Upload thumbnail to S3
   */
  const uploadThumbnail = async (): Promise<string> => {
    if (!thumbnail || !thumbnail?.file) {
      throw new Error("Thumbnail missing");
    }

    const thumbFileName = generateFileName(formik.values.id, "thumbnail", thumbnail.fileName || "jpg");

    const thumbRes:any = await dispatch(
      uploadSingleImage({
        file: thumbnail.file as File,
        folderName: "thumbnails",
        fileName: thumbFileName,
        mimetype: thumbnail.mimetype || "image/jpeg",
      })
    );

    if (thumbRes?.meta?.requestStatus !== "fulfilled") {
      throw new Error("Thumbnail upload failed");
    }

    const thumbUrl = thumbRes.payload?.preview || "";
    if (!thumbUrl) throw new Error("No thumbnail URL returned");

    return thumbUrl;
  };

   /**
   * Upload video to S3
   */
  const uploadVideo = async (): Promise<string> => {
    if (!videoFile) {
      throw new Error("Video missing");
    }

    const videoFileName = generateFileName(formik.values.id, "memories", videoFile.name);

    const videoRes:any = await dispatch(
      uploadShorts({
        file: videoFile,
        folderName: "memories",
        fileName: videoFileName,
        mimetype: videoFile.type,
      })
    );

    if (videoRes?.meta?.requestStatus !== "fulfilled") {
      throw new Error("Video upload failed");
    }

    const videoUrl = videoRes.payload?.preview || "";
    if (!videoUrl) throw new Error("No video URL returned");

    return videoUrl;
  };

  /**
   * Update formik with uploaded URLs, caption and submit
   */
  const submitWithUrls = async (thumbUrl: string, videoUrl: string) => {
    formik.setFieldValue("thumbnailUrl", thumbUrl);
    formik.setFieldValue("memoriesUrl", videoUrl);

    // Wait for formik to update state
    await new Promise((resolve) => setTimeout(resolve, 50));
    await formik.submitForm();
  };

  const handleFinalSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Upload both files
      const [thumbUrl, videoUrl] = await Promise.all([
        uploadThumbnail(),
        uploadVideo(),
      ]);

      // Submit form with URLs
      await submitWithUrls(thumbUrl, videoUrl);
      
      // Force refresh shorts list
      dispatch(forceRefreshShorts());

      setOpen(false);
      toast.success("Memories created successfully!");
    } catch (err: any) {
      console.error("[v0] Upload error:", err);
      toast.error(err?.message || "Upload failed");
      setStep(3);
    } finally {
      resetAll();
      setIsSubmitting(false);
    }
  };

  const modalWidth = isMobile ? 320 : isTablet ? 480 : 520
  const modalPadding = isMobile ? 2 : 3

  return (
    <DialogBox open={open} onClose={() => setOpen(false)}>
      <Box p={modalPadding} minWidth={modalWidth} sx={{ maxHeight: "90vh", overflowY: "auto" }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
            Create Memories
          </Typography>
          <Button
            variant="text"
            size={isMobile ? "small" : "medium"}
            onClick={() => {
              setOpen(false)
              resetAll()
            }}
          >
            Close
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={3} sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
          Upload step by step: Select merchant → Select trip → Upload thumbnail (9:16) → Select video (≤
          {MAX_VIDEO_SECONDS}s, ≤{MAX_VIDEO_SIZE_MB}MB) → Add caption (optional) → Review and submit.
        </Typography>

        {!isMobile && (
          <Stepper activeStep={step} alternativeLabel={!isTablet} sx={{ mb: 4 }}>
            {STEPS.map((s) => (
              <Step key={s}>
                <StepLabel sx={{ fontSize: isTablet ? "0.75rem" : "inherit" }}>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Mobile step indicator */}
        {isMobile && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: "background.default", textAlign: "center" }}>
            <Typography variant="body2" fontWeight={600}>
              Step {step + 1} of {STEPS.length}: {STEPS[step]}
            </Typography>
          </Paper>
        )}

        {/* Steps Content */}
        <Stack spacing={3}>
          {/* Step 0: Trip Selection */}
          {step === 0 && (
            <Box>
              <FormControl fullWidth>
                <InputLabel sx={{ fontWeight: 600, fontSize: "1rem" }}>Choose Trip</InputLabel>
                <Select
                  value={formik.values.tripId}
                  label="Choose Trip"
                  onChange={handleTripDetails}
                  IconComponent={KeyboardArrowDownIcon}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 2,
                        mt: 1,
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                        border: "1px solid",
                        borderColor: "divider",
                        maxHeight: 400,
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                  sx={{
                    minHeight: 60,
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      py: 2,
                      px: 2,
                      fontWeight: 500,
                    },
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
                    },
                  }}
                >
                  {/* Search Field */}
                  <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider", pointerEvents: "auto" }}>
                    <TextField
                      fullWidth
                      placeholder="Search trips by title..."
                      size="small"
                      value={tripSearchQuery}
                      onChange={(e) => setTripSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                          fontSize: "0.875rem",
                        },
                        "& .MuiOutlinedInput-input": {
                          py: 1,
                        },
                      }}
                    />
                  </Box>
                  
                  <MenuItem value="">
                    <em style={{ color: "#999" }}>Select a trip</em>
                  </MenuItem>
                  
                  {filteredTrips?.length === 0 ? (
                    <MenuItem disabled sx={{ color: "text.secondary" }}>
                      {trips?.length === 0 ? "No trips found" : "No matching trips"}
                    </MenuItem>
                  ) : (
                    filteredTrips?.map((t: any) => (
                      <MenuItem 
                        key={t.tripId ?? t.id} 
                        value={t.tripId ?? t.id}
                        sx={{
                          py: 1.5,
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "primary.light",
                            "&:hover": {
                              backgroundColor: "primary.light",
                            },
                          },
                        }}
                      >
                        <Box display={'flex'} gap={1}>
                          <Typography fontWeight={600} sx={{ fontSize: isMobile ? "0.875rem" : "1rem" }}>
                            {t.title ?? t.tripTitle}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: "block", 
                              mt: 0.25,
                              fontSize: isMobile ? "0.7rem" : "0.75rem"
                            }}
                          >
                           ( {t.nights} nights / {t.days} days)
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Step 2: Thumbnail Upload */}
          {step === 1 && (
            <Box>
              <Typography variant="subtitle2" mb={2}>
                Upload Thumbnail (Aspect Ratio: 9:16, Max Size: 100KB)
              </Typography>
              <ImageUploader
                label="Upload Thumbnail"
                images={thumbnail}
                setImages={setThumbnail}
                maxFileSize={100 * 1024}  
                maxFiles={1}
                handleUpload={() => {}}
                noUpload
                allowedAspectRatios={[THUMB_RATIO]}
                allowedRange={THUMB_RATIO_RANGE}
              />
            </Box>
          )}

          {/* Step 3: Video Selection */}
          {step === 2 && (
            <Box>
              <Typography variant="subtitle2" mb={2}>
                Select Video File (File Format: MP4, Max Size: {MAX_VIDEO_SIZE_MB}MB, Max Duration: {MAX_VIDEO_SECONDS}s)
              </Typography>
              <Button variant="outlined" component="label" fullWidth>
                Select Video
                <input type="file" accept="video/mp4" hidden onChange={handleVideoSelect} />  
              </Button>
              {videoFile && (
                <Box mt={2}>
                  <Typography variant="body2">
                    <strong>File:</strong> {videoFile.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {videoDuration ? Math.round(videoDuration) : "—"}s
                  </Typography>
                  <Box mt={1} display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setVideoFile(null)
                        setVideoDuration(null)
                      }}
                    >
                      Change Video
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Step 4: Caption */}
          {step === 3 && (
            <Box>
              <Typography variant="subtitle2" mb={2}>
                Add Caption (Optional, Max {MAX_CAPTION_LENGTH} characters)
              </Typography>
              <Box>
                <textarea
                  value={caption}
                  onChange={(e) => {
                    if (e.target.value.length <= MAX_CAPTION_LENGTH) {
                      const sanitized = sanitizeCaption(e.target.value);
                      setCaption(sanitized)
                    }
                  }}
                  placeholder="Add a caption for this Memories (optional)..."
                  style={{
                    width: "100%",
                    minHeight: "100px",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px solid #E9EAEB",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    fontWeight: 400,
                    resize: "vertical",
                    whiteSpace: "pre-wrap",
                  }}
                />
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {caption.length} / {MAX_CAPTION_LENGTH}
                  </Typography>
                  {caption.length === MAX_CAPTION_LENGTH && (
                    <Typography variant="caption" color="warning.main">
                      Character limit reached
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Step 5: Review */}
          {step === 4 && (
            <Box>
              <Typography variant="subtitle2" mb={2}>
                Review Before Submit
              </Typography>
              <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, border: "1px solid #E9EAEB" }}>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      Thumbnail:
                    </Typography>
                    <Typography variant="body2" color={thumbnail ? "success.main" : "error.main"}>
                      {thumbnail ? "✓ Ready" : "✗ Missing"}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      Video:
                    </Typography>
                    <Typography variant="body2" color={videoFile ? "success.main" : "error.main"}>
                      {videoFile ? "✓ Ready" : "✗ Missing"}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      Caption:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right", wordBreak: "break-word", maxWidth: "50%" }}>
                      {caption ? `"${caption.substring(0, 50)}${caption.length > 50 ? "..." : ""}"` : "(No caption)"}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      Status:
                    </Typography>
                    <Typography variant="body2">Pending Submission</Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        </Stack>

        {/* Navigation */}
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          justifyContent={isMobile ? "stretch" : "space-between"}
          mt={4}
        >
          <Button
            variant="outlined"
            fullWidth={isMobile}
            onClick={() => {
              setOpen(false)
              resetAll()
            }}
          >
            Cancel
          </Button>

          <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent={'flex-end'} flex={isMobile ? 0 : 1}>
            <Button disabled={step === 0} fullWidth={isMobile} onClick={() => setStep(step - 1)}>
              Back
            </Button>

            {step === 0 && (
              <Button variant="contained" fullWidth={isMobile} onClick={handleTripNext}>
                Next
              </Button>
            )}

            {step === 1 && (
              <Button variant="contained" fullWidth={isMobile} onClick={handleThumbnailNext} disabled={!thumbnail}>
                Next
              </Button>
            )}

            {step === 2 && (
              <Button variant="contained" fullWidth={isMobile} onClick={handleVideoNext} disabled={!videoFile}>
                Next
              </Button>
            )}

            {step === 3 && (
              <Button variant="contained" fullWidth={isMobile} onClick={handleCaptionNext}>
                Next
              </Button>
            )}

            {step === 4 && (
              <Button
                variant="contained"
                fullWidth={isMobile}
                onClick={handleFinalSubmit}
                disabled={isSubmitting || !thumbnail || !videoFile}
              >
                {isSubmitting ? <CircularProgress size={20} /> : "Submit for Review"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </DialogBox>
  )
}

export default ShortsModal
